import {
  AUTH_SESSION_CHANGING_EVENT,
  AUTH_SESSION_INVALID_EVENT,
  AUTH_SESSION_INVALIDATION_STORAGE_KEY,
  AUTH_STORAGE_KEY,
  authSessionMatchesInvalidation,
  parseStoredSessionValue,
} from './auth';

const CHANNELS = ['ifood', 'balcao', 'salao', 'site', 'pedidos10', '99food'];
const SNAPSHOT_TTL = 5 * 60 * 1000;
const CATALOG_TTL = 2 * 60 * 1000;
const MERCHANT_TTL = 60 * 1000;
const REQUEST_TIMEOUT = 15 * 1000;
let snapshot = null;
let generation = 0;
let owner = '';
let ownerSession = null;
const activeLoaders = new Set();

const sessionKey = (session) => {
  const tenant = String(session?.user?.companyId ?? '').trim();
  const token = String(session?.token || '').trim();
  const browser = String(session?.browserSessionId || '').trim();
  return tenant && token && browser ? JSON.stringify([tenant, token, browser]) : '';
};

const emptyState = () => ({
  channels: Object.fromEntries(CHANNELS.map((key) => [key, {
    orders: [], lastSync: null, verified: false, error: null, baseline: false,
  }])),
  catalog: { products: {}, options: {} },
  merchant: null,
  merchantVerified: false,
  merchantReadVerified: false,
  merchantError: null,
  restored: false,
});

// Memory only. A logout/session transition also prevents late requests from
// repopulating a snapshot, including a subsequent login to the same tenant.
export const invalidatePedidosSnapshots = () => {
  snapshot = null;
  owner = '';
  ownerSession = null;
  generation += 1;
  activeLoaders.forEach((dispose) => dispose());
};

if (typeof window !== 'undefined') {
  window.addEventListener(AUTH_SESSION_CHANGING_EVENT, invalidatePedidosSnapshots);
  window.addEventListener(AUTH_SESSION_INVALID_EVENT, invalidatePedidosSnapshots);
  window.addEventListener('storage', (event) => {
    if (event.key === AUTH_SESSION_INVALIDATION_STORAGE_KEY) {
      if (authSessionMatchesInvalidation(ownerSession, event.newValue)) invalidatePedidosSnapshots();
      return;
    }
    if (event.key === AUTH_STORAGE_KEY) {
      const next = parseStoredSessionValue(event.newValue);
      if (next && ownerSession
        && next.token === ownerSession.token
        && String(next.user?.companyId ?? '') === String(ownerSession.user?.companyId ?? '')
        && next.browserSessionId === ownerSession.browserSessionId) return;
      invalidatePedidosSnapshots();
    } else if (event.key === null) {
      invalidatePedidosSnapshots();
    }
  });
}

export const readPedidosSnapshot = (session) => {
  const key = sessionKey(session);
  if (!key || key !== owner) {
    if (owner) invalidatePedidosSnapshots();
    return null;
  }
  if (!snapshot || Date.now() - snapshot.savedAt >= SNAPSHOT_TTL) {
    snapshot = null;
    return null;
  }
  const result = emptyState();
  CHANNELS.forEach((channel) => {
    if (snapshot.times[channel] !== undefined
      && Date.now() - snapshot.times[channel] < SNAPSHOT_TTL) {
      result.channels[channel] = {
        ...snapshot.state.channels[channel], verified: false, baseline: false,
      };
    }
  });
  if (snapshot.times.catalog !== undefined
    && Date.now() - snapshot.times.catalog < SNAPSHOT_TTL) {
    result.catalog = snapshot.state.catalog;
  }
  if (snapshot.times.merchant !== undefined
    && Date.now() - snapshot.times.merchant < SNAPSHOT_TTL) {
    result.merchant = snapshot.state.merchant;
  }
  result.restored = true;
  return result;
};

export const createPedidosDataLoader = ({
  session, isCurrent, onChange = () => {}, onError = () => {}, requests,
}) => {
  const key = sessionKey(session);
  const restored = readPedidosSnapshot(session);
  owner = key;
  ownerSession = session;
  const ownGeneration = generation;
  let state = restored || emptyState();
  const times = restored && snapshot ? { ...snapshot.times } : {};
  const firstSuccess = new Set();
  const flights = new Map();
  const afterCurrentReads = new Map();
  let disposed = false;
  let merchantReadThisMount = false;
  let merchantRetryTimer = null;
  let merchantRetries = 0;
  let queuedSync = null;
  const current = () => !disposed && generation === ownGeneration && isCurrent();

  const publish = () => {
    if (!current()) return;
    if (key) snapshot = { key, savedAt: Date.now(), state, times: { ...times } };
    onChange(state);
  };

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    clearTimeout(merchantRetryTimer);
    flights.forEach((flight) => flight.cancel());
    activeLoaders.delete(dispose);
  };
  activeLoaders.add(dispose);

  const run = (resource, options = {}) => {
    if (!current() || typeof requests[resource] !== 'function') return Promise.resolve();
    const existing = flights.get(resource);
    if (existing) return existing.promise;
    if (resource === 'merchant' && options.force) {
      clearTimeout(merchantRetryTimer);
      merchantRetryTimer = null;
      merchantRetries = 0;
    }
    const controller = new AbortController();
    let timer;
    let cancel;
    const limit = new Promise((resolve, reject) => {
      cancel = () => {
        controller.abort();
        const error = new Error('Leitura cancelada');
        error.name = 'AbortError';
        reject(error);
      };
      timer = setTimeout(() => {
        const error = new Error('Tempo limite de leitura excedido');
        error.name = 'TimeoutError';
        // Settle the timeout first: a fetch abort must not hide its cause.
        reject(error);
        controller.abort();
      }, REQUEST_TIMEOUT);
    });
    const flight = { cancel, sync: Boolean(options.sync), promise: null };
    flight.promise = Promise.race([
      Promise.resolve().then(() => {
        if (!current() || controller.signal.aborted) {
          const error = new Error('Leitura cancelada');
          error.name = 'AbortError';
          throw error;
        }
        return requests[resource]({ ...options, signal: controller.signal });
      }),
      limit,
    ]).then((payload) => {
      if (!current()) return;
      times[resource] = Date.now();
      if (CHANNELS.includes(resource)) {
        const baseline = !firstSuccess.has(resource);
        firstSuccess.add(resource);
        state = {
          ...state,
          channels: {
            ...state.channels,
            [resource]: {
              orders: Array.isArray(payload) ? payload : (payload?.orders || []),
              lastSync: payload?.lastSync ?? null,
              verified: true, error: null, baseline,
            },
          },
        };
      } else if (resource === 'catalog') {
        state = { ...state, catalog: payload || { products: {}, options: {} } };
      } else {
        merchantReadThisMount = true;
        state = {
          ...state, merchant: payload, merchantVerified: !payload?.refreshPending,
          merchantReadVerified: true, merchantError: null,
        };
        clearTimeout(merchantRetryTimer);
        merchantRetryTimer = null;
        // status-summary schedules an upstream refresh after responding on a
        // cache miss. Re-read only its local snapshot, with a bounded retry.
        if (payload?.refreshPending && merchantRetries < 3) {
          merchantRetries += 1;
          merchantRetryTimer = setTimeout(() => {
            merchantRetryTimer = null;
            if (current()) run('merchant', { force: false });
          }, 2000);
        }
      }
      publish();
    }).catch((error) => {
      if (!current()) return;
      if (CHANNELS.includes(resource)) {
        state = {
          ...state,
          channels: {
            ...state.channels,
            [resource]: {
              ...state.channels[resource], verified: false,
              error: error.message || 'Falha de leitura', baseline: false,
            },
          },
        };
      } else if (resource === 'merchant') {
        state = {
          ...state, merchantVerified: false, merchantReadVerified: false,
          merchantError: error.message || 'Falha de leitura',
        };
      }
      publish();
      onError(resource, error);
    }).finally(() => {
      clearTimeout(timer);
      if (flights.get(resource) === flight) flights.delete(resource);
    });
    flights.set(resource, flight);
    return flight.promise;
  };

  const refreshIfood = ({ sync = false } = {}) => {
    const flight = flights.get('ifood');
    if (sync && flight && !flight.sync) {
      if (!queuedSync) {
        queuedSync = flight.promise.then(() => run('ifood', { sync: true }))
          .finally(() => { queuedSync = null; });
      }
      return queuedSync;
    }
    return run('ifood', { sync });
  };

  const refreshMerchant = ({ force = false } = {}) => {
    if (flights.has('merchant')) {
      return force ? readAfterCurrent('merchant', { force: true }) : flights.get('merchant').promise;
    }
    if (!force && merchantReadThisMount && times.merchant !== undefined
      && Date.now() - times.merchant < MERCHANT_TTL) return Promise.resolve();
    clearTimeout(merchantRetryTimer);
    merchantRetryTimer = null;
    merchantRetries = 0;
    return run('merchant', { force });
  };

  const readAfterCurrent = (resource, options) => {
    const flight = flights.get(resource);
    if (!flight) return run(resource, options);
    const queued = afterCurrentReads.get(resource);
    if (queued?.after === flight) {
      // If any mutation needs official sync, the shared fresh read does too.
      queued.options.sync = queued.options.sync || options.sync;
      return queued.promise;
    }
    const next = { after: flight, options: { ...options }, promise: null };
    next.promise = flight.promise.then(() => run(resource, next.options)).finally(() => {
      if (afterCurrentReads.get(resource) === next) afterCurrentReads.delete(resource);
    });
    afterCurrentReads.set(resource, next);
    return next.promise;
  };

  const refresh = ({ syncIfood = false, afterCurrent = false } = {}) => {
    if (!current()) return Promise.resolve();
    if (times.catalog === undefined || Date.now() - times.catalog >= CATALOG_TTL) {
      run('catalog');
    }
    refreshMerchant();
    return Promise.all(CHANNELS.map((channel) => {
      if (afterCurrent) return readAfterCurrent(channel, channel === 'ifood' ? { sync: syncIfood } : {});
      return channel === 'ifood' ? refreshIfood({ sync: syncIfood }) : run(channel);
    }));
  };

  return { getState: () => state, refresh, refreshIfood, refreshMerchant, dispose };
};
